import * as React from 'react';
import Chart from 'chart.js';
import { TICKER_FLOW_PAGE } from 'constants/pages';
import { themeDark } from 'styles/theme';
import { BarChartProps } from './props';

const getSortValue = (trade: BarChartProps['topTrades'][number], sortKey: string) =>
  Number(trade[sortKey as keyof BarChartProps['topTrades'][number]] || 0);

const getTooltipTitle = (item: Chart.ChartTooltipItem[], data: Chart.ChartData, label: string) => {
  const itemIndex = item[0]?.index;
  const tickerLabel =
    typeof itemIndex === 'number' && data.labels ? data.labels[itemIndex] || '' : '';

  return `${tickerLabel} ${label}`;
};

type ChartClickItem = {
  _model?: {
    label?: string | number;
  };
};

const BarChartTopTickers: React.FC<React.PropsWithChildren<BarChartProps>> = ({
  topTrades: displayedTrades,
  sortKey,
  color,
  prefix,
  postfix,
  label,
  formatter,
  useSignedColors = false,
  bullishColor = themeDark.color.graphCall,
  bearishColor = themeDark.color.graphPut,
}) => {
  const chartWrapperRef = React.useRef<HTMLCanvasElement>(null);
  const chartRef = React.useRef<Chart | undefined>(undefined);

  React.useEffect(() => {
    const myChartRef = chartRef.current;
    if (myChartRef?.data.datasets) {
      const primaryDataset = myChartRef.data.datasets[0];
      primaryDataset.data = displayedTrades.map((t) => getSortValue(t, sortKey));
      primaryDataset.backgroundColor = useSignedColors
        ? displayedTrades.map((t) => (getSortValue(t, sortKey) >= 0 ? bullishColor : bearishColor))
        : color;
      myChartRef.data.labels = displayedTrades.map((t) => t.ticker);
      const tooltipCallbacks = myChartRef.options.tooltips?.callbacks;
      if (tooltipCallbacks) {
        tooltipCallbacks.title = function (item, data) {
          return getTooltipTitle(item, data, label);
        };
        tooltipCallbacks.label = function (item) {
          const value = Number(item.value);
          const sign = useSignedColors && value > 0 ? '+' : '';
          return `${sign}${prefix}${Math.round(value)}${postfix}`;
        };
      }
      const yAxis = myChartRef.options.scales?.yAxes?.[0];
      if (yAxis?.scaleLabel) {
        yAxis.scaleLabel.labelString = label;
      }
      if (yAxis?.ticks) {
        yAxis.ticks.callback = function (value) {
          return `${prefix}${formatter(Number(value))}${postfix}`;
        };
      }
      myChartRef.update();
    }
  }, [
    displayedTrades,
    sortKey,
    color,
    label,
    prefix,
    postfix,
    formatter,
    useSignedColors,
    bullishColor,
    bearishColor,
  ]);

  React.useEffect(() => {
    const myChartRef = chartWrapperRef.current?.getContext('2d');

    if (myChartRef) {
      chartRef.current = new Chart(myChartRef, {
        type: 'bar',
        data: {
          datasets: [
            {
              type: 'bar',
              label: 'Contracts',
              data: displayedTrades.map((t) => getSortValue(t, sortKey)),
              backgroundColor: useSignedColors
                ? displayedTrades.map((t) =>
                    getSortValue(t, sortKey) >= 0 ? bullishColor : bearishColor,
                  )
                : color,
              // fill: true,
              // borderWidth: 1,
              // borderColor: '#2a587f',
              // hoverBackgroundColor: '#4c87b9',
              // hoverBorderColor: '#2a587f',
            },
          ],
          labels: displayedTrades.map((t) => t.ticker),
        },
        options: {
          legend: {
            display: false,
          },
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            animateScale: true,
            animateRotate: true,
          },
          tooltips: {
            callbacks: {
              title: function (item, data) {
                return getTooltipTitle(item, data, label);
              },
              label: function (item) {
                const value = Number(item.value);
                const sign = useSignedColors && value > 0 ? '+' : '';
                return `${sign}${prefix}${Math.round(value)}${postfix}`;
              },
            },
          },
          scales: {
            yAxes: [
              {
                scaleLabel: {
                  display: true,
                  labelString: label,
                  fontColor: themeDark.color.textLight,
                  fontSize: 11,
                },
                ticks: {
                  callback: function (value) {
                    return `${prefix}${formatter(Number(value))}${postfix}`;
                  },
                  fontColor: themeDark.color.textLight,
                  fontSize: 11,
                },
              },
            ],
            xAxes: [
              {
                ticks: {
                  fontColor: themeDark.color.textLight,
                  fontSize: 10,
                },
              },
            ],
          },
          onClick: function (_e, items) {
            if (!items || !items.length) {
              return;
            }
            const [firstItem] = items as ChartClickItem[];
            const ticker = firstItem?._model?.label;
            if (!ticker) {
              return;
            }
            window.open(`${TICKER_FLOW_PAGE}/${ticker}`, '_blank');
          },
        },
      });
    }

    return () => {
      chartRef.current?.destroy();
    };
  }, []);

  return (
    <div
      style={{
        maxHeight: '100%',
        height: '100%',
        width: '100%',
      }}
    >
      <canvas ref={chartWrapperRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default BarChartTopTickers;
